/* ===================================================================
   Rhody Reviews api/v1 -- text opt-in domain (Penn Condition 1, 2026-07-29).

   Binds the three routes in engine/api/v1/sms_optin.py:

     GET  /sms/opt-in/customers
     POST /sms/opt-in/customers/<id>
     POST /sms/opt-in/customers/<id>/send-confirmation

   There is DELIBERATELY no bulk method here, because there is no bulk route.
   Our registered A2P message flow says the confirmation text goes only to
   customers who checked an optional box at the point of service, so a
   "confirm everyone" call would be an unsolicited send to whoever on the list
   did not. One customer per call.

   Everything user-visible (headline, explanation) is computed SERVER-side and
   passed through verbatim. The screen never invents a reason a customer cannot
   be texted, and never shows an error code.
   =================================================================== */
import { request } from './_http';

/* The funnel stages, mirrored from compliance/sms_optin_capture.py. */
export type OptInStage =
  | 'no_phone'
  | 'invalid_phone'
  | 'do_not_contact'
  | 'opted_out'
  | 'no_opt_in_on_file'
  | 'ready_to_confirm'
  | 'awaiting_reply'
  | 'no_reply'
  | 'confirmed'
  | 'expired';

/* Where and when the customer asked. This is the provenance that makes the
   opt-in a TCPA defense rather than a checkbox. */
export interface OptInCapture {
  captured: boolean;
  source: string | null;
  source_label: string | null;
  at: string | null;
  recorded_by: string | null;
  note: string | null;
}

export interface OptInCustomer {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
}

export interface OptInEntry {
  customer: OptInCustomer;
  stage: OptInStage;
  /* Short human label, e.g. "Waiting on their YES". Server-authored. */
  headline: string;
  /* A plain-English sentence explaining what is true and what happens next. */
  explanation: string;
  may_send_confirmation: boolean;
  may_send_review_request: boolean;
  phone_e164: string | null;
  opt_in: OptInCapture;
  consent_state: string | null;
  confirmation_attempts: number;
}

export interface OptInSummary {
  total: number;
  counts: Record<string, number>;
  ready_to_confirm: number;
  awaiting_reply: number;
  confirmed: number;
  textable: number;
  no_opt_in_on_file: number;
}

export interface OptInSourceChoice {
  value: string;
  label: string;
}

export interface OptInOverview {
  client_id: string;
  summary: OptInSummary;
  customers: OptInEntry[];
  account: {
    /* The business turned texting on for its account. */
    sms_authorized: boolean;
    /* The platform-wide texting switch. False today. */
    texting_live: boolean;
  };
  opt_in_sources: OptInSourceChoice[];
}

export interface RecordOptInInput {
  opted_in: boolean;
  /* Required when opting in: the owner affirming the customer asked. */
  confirm?: boolean;
  source?: string;
  note?: string;
}

export interface RecordOptInResult extends OptInEntry {
  client_id: string;
}

export interface SendConfirmationResult {
  client_id: string;
  customer: OptInCustomer;
  sent: boolean;
  /* True while the platform texting switch is off: the exact message is
     returned for review and NOTHING is transmitted. */
  dry_run: boolean;
  to: string | null;
  body: string;
  message_id?: string | null;
  stage: OptInStage;
  headline?: string;
  detail?: string;
  sent_at?: string;
}

export const smsOptInApi = {
  getOptInOverview(signal?: AbortSignal): Promise<OptInOverview> {
    return request<OptInOverview>('/api/v1/sms/opt-in/customers', { signal });
  },

  recordOptIn(customerId: string, input: RecordOptInInput): Promise<RecordOptInResult> {
    return request<RecordOptInResult>(
      `/api/v1/sms/opt-in/customers/${encodeURIComponent(customerId)}`,
      { method: 'POST', body: input },
    );
  },

  /* Sends the ONE "Reply YES to confirm..." message to ONE customer.
     While texting is switched off this returns dry_run: true and makes no
     Twilio call at all. */
  sendOptInConfirmation(customerId: string): Promise<SendConfirmationResult> {
    return request<SendConfirmationResult>(
      `/api/v1/sms/opt-in/customers/${encodeURIComponent(customerId)}/send-confirmation`,
      { method: 'POST' },
    );
  },
};
